import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { buscarSlaOficina, FiltrosOficina, RespostaSla } from '../../../api/dashboardOficinaApi';
import DetalheTabelaModal from '../../../shared/ui/DetalheTabelaModal';
import TabelaPaginada from '../../../shared/ui/TabelaPaginada';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import { notify } from '../../../shared/feedback/notify';
import { corDaSerie, corOrdinal, formatarNumero, TINTA_GRAFICO } from '../../../shared/ui/chart-palette';
import {
  AreaEstado,
  CardGrafico,
  EIXO_LINHA,
  EIXO_TICK,
  ESTILO_TOOLTIP,
  Kpi,
  Legenda,
} from '../../../shared/ui/GraficoBase';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/dashboard-base.css';

const COR_PERMANENCIA = corDaSerie(0);
const COR_TRABALHO = corDaSerie(1);
const COR_DENTRO = corDaSerie(3);
const COR_FORA = corDaSerie(5);

export const helpSectionsSla: ComoFuncionaSecao[] = [
  {
    titulo: 'As duas medidas de tempo, e por que as duas aparecem',
    conteudo: (
      <>
        <p>
          <strong>Permanência</strong> é o tempo que o <strong>veículo</strong> ficou parado na
          esteira — relógio de parede, incluindo espera por peça, por autorização e fim de semana.
          Mede <strong>espera</strong>.
        </p>
        <p>
          <strong>Trabalho</strong> é o tempo que a <strong>equipe</strong> gastou na esteira,
          somando as atividades registradas. Mede <strong>capacidade</strong>.
        </p>
        <p>
          Um veículo com cinco dias em Funilaria e seis horas de trabalho é o caso normal, não uma
          anomalia. Olhar só a permanência cobra da equipe uma espera que não é dela; olhar só o
          trabalho esconde o veículo parado há duas semanas.
        </p>
      </>
    ),
  },
  {
    titulo: 'O tempo de trabalho já desconta as pausas',
    conteudo: (
      <>
        <p>
          Da duração de cada atividade são subtraídas as pausas — as que o colaborador registrou, as
          previstas (almoço e lanche) e o período em que a <strong>oficina esteve fechada</strong>.
        </p>
        <p>
          Por isso o número aqui é menor do que a diferença entre início e fim das atividades. É
          proposital: sem o desconto, uma hora de almoço por dia apareceria como tempo produtivo.
        </p>
      </>
    ),
  },
  {
    titulo: 'Oficina fechada não conta como pausa',
    conteudo: (
      <>
        <p>
          O indicador <strong>Tempo em pausa</strong> considera apenas descanso de verdade: almoço,
          lanche e as pausas que o colaborador registrou. O tempo de{' '}
          <strong>oficina fechada</strong> — madrugada, depois do expediente e dias sem
          funcionamento — fica fora da conta, e o percentual é calculado sobre o tempo que caiu{' '}
          <strong>dentro</strong> do horário de funcionamento.
        </p>
        <p>
          Se ele entrasse, uma atividade deixada aberta na sexta e finalizada na segunda apareceria
          com dezenas de horas de "pausa" que ninguém tirou, e o indicador viraria medida de
          esquecimento em vez de descanso.
        </p>
        <p>
          Esse tempo aparece <strong>à parte</strong>, embaixo da composição das pausas, porque
          sozinho ele diz outra coisa: quanta atividade está sendo esquecida em aberto.
        </p>
      </>
    ),
  },
  {
    titulo: 'Horas totais e média por atividade respondem coisas diferentes',
    conteudo: (
      <>
        <p>
          Em <strong>Mão de obra por esteira</strong>, as <strong>horas totais</strong> dizem quanto
          de equipe cada etapa consumiu no recorte — é o número de gargalo, e por ele a tabela está
          ordenada.
        </p>
        <p>
          A <strong>média por atividade</strong> diz quanto custa uma passagem típica. Uma etapa
          pode ter média alta e consumir pouco, se teve poucas atividades: olhe sempre a coluna{' '}
          <strong>Atividades</strong> antes de concluir alguma coisa a partir da média.
        </p>
      </>
    ),
  },
  {
    titulo: 'Somar duas pessoas no mesmo veículo dobra o tempo',
    conteudo: (
      <p>
        O tempo de trabalho é <strong>hora de pessoa</strong>: dois colaboradores durante uma hora
        no mesmo veículo contam <strong>duas horas</strong>. É o que a oficina gastou de mão de
        obra, e é diferente do tempo que o veículo esperou — que continua sendo uma hora.
      </p>
    ),
  },
  {
    titulo: 'Tempo por colaborador não é ranking de esforço',
    conteudo: (
      <>
        <p>
          Quem trabalha em esteiras mais demoradas acumula mais horas naturalmente, e quem entrou
          há pouco tem menos. Compare <strong>dentro da mesma esteira</strong>, usando a tabela por
          colaborador e esteira — é para isso que ela existe.
        </p>
        <p>
          A coluna <strong>% em pausa</strong> é sobre a atividade, não sobre a pessoa: ela inclui
          o almoço descontado automaticamente.
        </p>
      </>
    ),
  },
  {
    titulo: 'Cumprimento de prazo (é histórico, não é o agora)',
    conteudo: (
      <>
        <p>
          Cada passagem de um veículo por uma esteira guarda o <strong>prazo previsto naquele
          momento</strong>. O gráfico compara o tempo real com aquele prazo — não com o prazo
          configurado hoje, que pode ter mudado depois.
        </p>
        <p>
          Só entram <strong>passagens já encerradas</strong>. Um veículo que está estourando o
          prazo <strong>neste momento</strong> não aparece aqui — ele só entra quando sair da
          esteira. Para o que está atrasado agora, use a aba <strong>Prazos</strong>.
        </p>
        <p>
          Passagens <strong>sem prazo definido</strong> aparecem à parte: elas não são atraso, são
          ausência de combinado.
        </p>
      </>
    ),
  },
  {
    titulo: 'O que entra na conta',
    conteudo: (
      <p>
        Apenas atividades <strong>finalizadas</strong> e passagens de esteira já{' '}
        <strong>encerradas</strong> — o que ainda está em andamento não tem duração final e
        distorceria a média. Esteiras de fluxo livre (as que não têm serviço, como Aguardando e
        Orçamento) ficam de fora dos tempos de trabalho.
      </p>
    ),
  },
];

/**
 * As três situações de prazo, na ordem em que a pilha do gráfico as empilha.
 *
 * ⚠️ "Sem prazo definido" NÃO é atraso: é passagem que nunca teve combinado. Somá-la ao "fora
 * do prazo" inventaria um descumprimento que ninguém acordou — daí a aba própria e o texto.
 */
const ABAS_PRAZO = [
  {
    id: 'DENTRO' as const,
    rotulo: 'Dentro do prazo',
    apoio: 'Passagens que terminaram dentro do prazo previsto naquele momento.',
  },
  {
    id: 'FORA' as const,
    rotulo: 'Fora do prazo',
    apoio: 'Passagens que passaram do prazo previsto. Compare o tempo com o prazo ao lado.',
  },
  {
    id: 'SEM_PRAZO' as const,
    rotulo: 'Sem prazo definido',
    apoio:
      'Passagens que não tinham prazo combinado. Não são atraso — são ausência de combinado.',
  },
];

interface Props {
  filtros: FiltrosOficina;
  onJanela?: (janela: { dataInicio: string; dataFim: string }) => void;
  sidebarRef?: React.RefObject<HTMLElement>;
}

const SlaTab: React.FC<Props> = ({ filtros, onJanela, sidebarRef }) => {
  const [dados, setDados] = useState<RespostaSla | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [esteiraFoco, setEsteiraFoco] = useState<string>('');
  /** Esteira aberta no detalhamento de prazo, com a aba escolhida. */
  const [prazoFoco, setPrazoFoco] = useState<{
    esteira: string;
    passagens: RespostaSla['cumprimentoSla'][number]['passagens'];
  } | null>(null);
  const [abaPrazo, setAbaPrazo] = useState<'DENTRO' | 'FORA' | 'SEM_PRAZO'>('DENTRO');

  /** Colaborador aberto no detalhamento — mostra onde as horas dele foram gastas. */
  const [colaboradorFoco, setColaboradorFoco] = useState<{ id: string; nome: string } | null>(
    null,
  );

  const carregar = useCallback(
    async (f: FiltrosOficina) => {
      setCarregando(true);
      try {
        const resposta = await buscarSlaOficina(f);
        /*
         * ⚠️ Resposta sem o formato esperado é tratada como FALHA, não como dado.
         *
         * Acontece de verdade quando o servidor em execução é anterior à versão da tela (campo
         * renomeado, por exemplo). Sem esta guarda a leitura estoura em `undefined.algo` e
         * derruba a página com erro de runtime — tela branca, que é pior que número faltando.
         */
        if (!resposta || typeof resposta.porEsteira === 'undefined') {
          throw new Error('Resposta do servidor em formato inesperado');
        }

        setDados(resposta);
        setErro(null);
        onJanela?.({ dataInicio: resposta.janela.dataInicio, dataFim: resposta.janela.dataFim });
      } catch (e) {
        setDados(null);
        setErro(
          'Não foi possível carregar os tempos da oficina. Enquanto este aviso estiver na tela, não há número para ler aqui — recarregue a página; se persistir, acione a TI.',
        );
        notify.errorFrom(
          e,
          'Não foi possível carregar os tempos da oficina. Recarregue a página; se persistir, acione a TI.',
          { toastId: 'dashboard-oficina-sla-falhou' },
        );
      } finally {
        setCarregando(false);
      }
    },
    [onJanela],
  );

  useEffect(() => {
    carregar(filtros);
  }, [filtros, carregar]);

  const respondeu = dados !== null;
  const primeiraCarga = carregando && !respondeu;
  const textoVazio = respondeu
    ? 'Nenhuma atividade registrada neste recorte.'
    : 'Não foi possível carregar — veja o aviso acima.';

  const porEsteira = dados?.porEsteira ?? [];

  /*
   * ⚠️ Cópia antes de ordenar: `sort` muta o array, e `porEsteira` é a mesma referência que
   * alimenta o gráfico ao lado — ordenar no lugar reordenaria as barras, que seguem a ordem do
   * PROCESSO (Desmontagem → Funilaria → Pintura...) de propósito.
   */
  const maoDeObraPorEsteira = useMemo(
    () => [...(dados?.porEsteira ?? [])].sort((a, b) => b.horasTrabalhadas - a.horasTrabalhadas),
    [dados],
  );
  const porColaborador = dados?.porColaborador ?? [];
  // Mesmo motivo do `porBairro` da aba Região: `?? []` é referência nova a cada render.
  const matriz = useMemo(() => dados?.matrizColaboradorEsteira ?? [], [dados]);
  const cumprimento = dados?.cumprimentoSla ?? [];

  /** Esteiras que a matriz conhece — a lista do seletor de foco sai daqui, não do cadastro. */
  /**
   * Abre o detalhe de prazo JÁ na aba da faixa clicada.
   *
   * ⚠️ Quem clica no verde quer ver quem cumpriu — abrir sempre na primeira aba obrigaria a pessoa
   * a refazer na mão o filtro que o clique já expressou.
   */
  const abrirPrazo = (
    ponto: RespostaSla['cumprimentoSla'][number] | undefined,
    aba: 'DENTRO' | 'FORA' | 'SEM_PRAZO',
  ) => {
    if (!ponto?.passagens?.length) return;
    setAbaPrazo(aba);
    setPrazoFoco({ esteira: ponto.esteira, passagens: ponto.passagens });
  };

  const esteirasDaMatriz = useMemo(() => {
    const vistas = new Map<string, string>();
    matriz.forEach((linha) => vistas.set(linha.esteiraId, linha.esteira));
    return Array.from(vistas, ([id, nome]) => ({ id, nome }));
  }, [matriz]);

  const matrizFiltrada = useMemo(
    () => (esteiraFoco ? matriz.filter((linha) => linha.esteiraId === esteiraFoco) : matriz),
    [matriz, esteiraFoco],
  );

  /**
   * ⚠️ "Fora do expediente" NÃO entra na conta de pausa: é a oficina fechada, descontada de
   * sessões que ficaram abertas. Aparece como leitura à parte, porque sozinha diz outra coisa —
   * gente esquecendo de finalizar atividade.
   */
  const pausaTotal = (dados?.pausas.manualHoras ?? 0) + (dados?.pausas.janelaHoras ?? 0);

  return (
    <div className={carregando && respondeu ? 'do-atualizando' : undefined}>
      {erro && (
        <div className="erro-de-tela" role="alert">
          {erro}
        </div>
      )}

      <div className="do-aba-cabecalho">
        <div className="do-aba-titulo">
          Tempos &amp; Equipe
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
      </div>

      <p className="do-nota-janela">
        <strong>Permanência</strong> é quanto o veículo esperou; <strong>trabalho</strong> é quanto
        a equipe gastou. São medidas diferentes da mesma esteira, e por isso aparecem lado a lado.
      </p>

      <div className="do-kpis">
        <Kpi
          rotulo="Horas trabalhadas"
          valor={respondeu ? `${formatarNumero(dados.kpis.horasTrabalhadas)} h` : '-'}
          apoio="Soma de horas de pessoa, já sem as pausas"
          carregando={primeiraCarga}
        />
        <Kpi
          rotulo="Atividades finalizadas"
          valor={respondeu ? formatarNumero(dados.kpis.sessoesFinalizadas) : '-'}
          apoio="No recorte selecionado"
          carregando={primeiraCarga}
        />
        <Kpi
          rotulo="Colaboradores"
          valor={respondeu ? formatarNumero(dados.kpis.colaboradoresAtivos) : '-'}
          apoio="Com atividade registrada no recorte"
          carregando={primeiraCarga}
        />
        <Kpi
          rotulo="Horas a aprovar"
          valor={respondeu ? `${formatarNumero(dados.kpis.horasAguardandoAprovacao)} h` : '-'}
          apoio="Na fila do Chefe de Oficina"
          carregando={primeiraCarga}
        />
        <Kpi
          rotulo="Serviços inacabados"
          valor={respondeu ? formatarNumero(dados.kpis.servicosInacabados) : '-'}
          apoio="Finalizados sem concluir o serviço"
          carregando={primeiraCarga}
        />
        <Kpi
          rotulo="Tempo em pausa"
          valor={respondeu ? `${formatarNumero(dados.kpis.horasPausa)} h` : '-'}
          apoio={
            respondeu
              ? `${dados.kpis.percentualPausa}% do tempo dentro do horário da oficina`
              : 'Almoço, lanche e pausas registradas'
          }
          carregando={primeiraCarga}
        />
      </div>

      {/*
        ⚠️ Os dois gráficos POR ESTEIRA dividem a linha: são a mesma leitura vista de dois
        ângulos (tempo e prazo), e lado a lado a comparação é imediata. A tabela de colaboradores
        vai para a faixa inteira — cinco colunas espremidas em meia tela viram reticências.
      */}
      <div className="do-grid">
        <CardGrafico
          titulo="Espera x trabalho por esteira"
          descricao="Barras altas em permanência com trabalho baixo indicam veículo esperando, não equipe ocupada."
        >
          <Legenda
            itens={[
              { rotulo: 'Permanência do veículo (h)', cor: COR_PERMANENCIA },
              { rotulo: 'Trabalho da equipe (h)', cor: COR_TRABALHO },
            ]}
          />
          <div className="do-plot-alto">
            {!respondeu || porEsteira.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={porEsteira} margin={{ left: 8, right: 16 }}>
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} vertical={false} />
                  <XAxis dataKey="esteira" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} />
                  <YAxis tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} />
                  <Tooltip
                    contentStyle={ESTILO_TOOLTIP}
                    formatter={(valor: number, nome: string) => [`${valor} h`, nome]}
                  />
                  <Bar dataKey="permanenciaMediaHoras" name="Permanência do veículo (h)" fill={COR_PERMANENCIA} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="trabalhoMedioHoras" name="Trabalho da equipe (h)" fill={COR_TRABALHO} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardGrafico>
      </div>

      {/*
        ⚠️ Tabela, e não mais uma série no gráfico ao lado: o total (dezenas/centenas de horas) e as
        médias (unidades) têm ordens de grandeza diferentes, e no mesmo eixo a barra do total
        achataria as outras duas até virarem linha.

        Ordenada por TOTAL, decrescente — a pergunta que ela responde é "qual etapa consome mais
        mão de obra", e a resposta tem de estar na primeira linha.
      */}
      <div className="do-grid-largo">
        <CardGrafico
          titulo="Mão de obra por esteira"
          descricao="Horas de equipe somadas em cada etapa, da que mais consome para a que menos consome. A média por atividade responde outra pergunta: quanto custa uma passagem típica."
        >
          <div className="do-tabela-wrapper">
            {!respondeu || porEsteira.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <TabelaPaginada
                itens={maoDeObraPorEsteira}
                chave={(linha) => linha.esteiraId}
                vazio="Nenhuma atividade registrada neste recorte."
                colunas={[
                  { titulo: 'Esteira', largura: 220, texto: (l) => l.esteira },
                  {
                    titulo: 'Horas totais',
                    largura: 130,
                    texto: (l) => `${formatarNumero(l.horasTrabalhadas)} h`,
                  },
                  { titulo: 'Atividades', largura: 120, texto: (l) => formatarNumero(l.sessoes) },
                  {
                    titulo: 'Média por atividade',
                    largura: 170,
                    texto: (l) => `${formatarNumero(l.trabalhoMedioHoras)} h`,
                  },
                ]}
              />
            )}
          </div>
        </CardGrafico>
      </div>

      <div className="do-grid-largo">
        <CardGrafico
          titulo="Horas por colaborador"
          descricao="Soma de horas de pessoa no recorte. Quem atua em esteiras mais demoradas acumula mais horas — compare dentro da mesma esteira. Clique num nome para ver onde as horas foram trabalhadas."
        >
          <div className="do-tabela-wrapper">
            {!respondeu || porColaborador.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <TabelaPaginada
                itens={porColaborador}
                chave={(linha) => linha.colaboradorId}
                onClickLinha={(linha) =>
                  setColaboradorFoco({ id: linha.colaboradorId, nome: linha.colaborador })
                }
                vazio="Nenhum colaborador com atividade neste recorte."
                colunas={[
                  { titulo: 'Colaborador', largura: 240, texto: (l) => l.colaborador },
                  { titulo: 'Horas', largura: 110, texto: (l) => `${formatarNumero(l.horasTrabalhadas)} h` },
                  { titulo: 'Atividades', largura: 110, texto: (l) => formatarNumero(l.sessoes) },
                  { titulo: 'Média por atividade', largura: 160, texto: (l) => `${l.mediaHorasPorSessao} h` },
                  { titulo: 'Horas em pausa', largura: 150, texto: (l) => `${formatarNumero(l.horasPausa)} h` },
                  { titulo: '% em pausa', largura: 120, texto: (l) => `${l.percentualPausa}%` },
                ]}
              />
            )}
          </div>
        </CardGrafico>

        <CardGrafico
          titulo="Cumprimento de prazo por esteira"
          descricao="Compara cada passagem com o prazo previsto naquele momento. Sem prazo definido não é atraso — é ausência de combinado. Clique numa faixa para ver as passagens."
        >
          <Legenda
            itens={[
              { rotulo: 'Dentro do prazo', cor: COR_DENTRO },
              { rotulo: 'Fora do prazo', cor: COR_FORA },
              { rotulo: 'Sem prazo definido', cor: TINTA_GRAFICO.grade },
            ]}
          />
          <div className="do-plot">
            {!respondeu || cumprimento.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cumprimento} margin={{ left: 8, right: 16 }}>
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} vertical={false} />
                  <XAxis dataKey="esteira" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} />
                  <YAxis tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={ESTILO_TOOLTIP} />
                  {/*
                    Cada faixa da pilha abre o detalhe JÁ na aba dela: quem clica no verde quer
                    ver os que cumpriram, não uma lista misturada que ele precisa filtrar.
                  */}
                  <Bar
                    dataKey="dentroDoPrazo"
                    name="Dentro do prazo"
                    stackId="p"
                    fill={COR_DENTRO}
                    cursor="pointer"
                    onClick={(evento: any) => abrirPrazo(evento?.payload, 'DENTRO')}
                  />
                  <Bar
                    dataKey="foraDoPrazo"
                    name="Fora do prazo"
                    stackId="p"
                    fill={COR_FORA}
                    cursor="pointer"
                    onClick={(evento: any) => abrirPrazo(evento?.payload, 'FORA')}
                  />
                  <Bar
                    dataKey="semPrazoDefinido"
                    name="Sem prazo definido"
                    stackId="p"
                    fill={TINTA_GRAFICO.grade}
                    cursor="pointer"
                    onClick={(evento: any) => abrirPrazo(evento?.payload, 'SEM_PRAZO')}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardGrafico>
      </div>

      <div className="do-grid-largo">
        <CardGrafico
          titulo="Tempo por colaborador em cada esteira"
          descricao="A leitura que responde por ofício: quanto tempo cada pessoa gasta na esteira escolhida, e quantas atividades fez nela."
        >
          <div className="do-controles" style={{ marginBottom: 12 }}>
            <label htmlFor="sla-esteira-foco" style={{ marginRight: 8 }}>
              Esteira:
            </label>
            <select
              id="sla-esteira-foco"
              className="premium-text-input"
              value={esteiraFoco}
              onChange={(e) => setEsteiraFoco(e.target.value)}
              disabled={esteirasDaMatriz.length === 0}
            >
              <option value="">Todas</option>
              {esteirasDaMatriz.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nome}
                </option>
              ))}
            </select>
          </div>
          <div className="do-tabela-wrapper">
            {!respondeu || matrizFiltrada.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <TabelaPaginada
                itens={matrizFiltrada}
                chave={(l) => `${l.esteiraId}-${l.colaboradorId}`}
                vazio="Nenhuma atividade nesta esteira no recorte."
                colunas={[
                  { titulo: 'Esteira', largura: 180, texto: (l) => l.esteira },
                  { titulo: 'Colaborador', largura: 240, texto: (l) => l.colaborador },
                  { titulo: 'Horas', largura: 110, texto: (l) => `${formatarNumero(l.horasTrabalhadas)} h` },
                  { titulo: 'Atividades', largura: 110, texto: (l) => formatarNumero(l.sessoes) },
                  { titulo: 'Média por atividade', largura: 160, texto: (l) => `${l.mediaHorasPorSessao} h` },
                ]}
              />
            )}
          </div>
        </CardGrafico>
      </div>

      <div className="do-grid">
        <CardGrafico
          titulo="Composição das pausas"
          descricao="Almoço e lanche são descontados pelo sistema nos horários cadastrados; manuais são as que o colaborador registrou. A oficina fechada não entra aqui — aparece embaixo, e é outra leitura."
        >
          <div className="do-plot">
            {!respondeu || pausaTotal === 0 ? (
              <AreaEstado
                carregando={primeiraCarga}
                mensagem={respondeu ? 'Nenhuma pausa registrada neste recorte.' : textoVazio}
              />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[
                    { tipo: 'Almoço e lanche', horas: dados.pausas.janelaHoras },
                    { tipo: 'Manuais', horas: dados.pausas.manualHoras },
                  ]}
                  layout="vertical"
                  margin={{ left: 8, right: 16 }}
                >
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} horizontal={false} />
                  <XAxis type="number" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} />
                  <YAxis type="category" dataKey="tipo" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} width={110} />
                  <Tooltip contentStyle={ESTILO_TOOLTIP} formatter={(valor: number) => [`${valor} h`, 'Horas']} />
                  <Bar dataKey="horas" fill={corOrdinal(1, 3)} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          {respondeu && dados.pausas.foraExpedienteHoras > 0 && (
            <p className="do-nota-janela">
              Fora do horário de funcionamento:{' '}
              <strong>{formatarNumero(dados.pausas.foraExpedienteHoras)} h</strong> descontadas de
              atividades que ficaram abertas depois que a oficina fechou. Isso{' '}
              <strong>não é pausa</strong> de ninguém — número alto significa atividade que o
              colaborador esqueceu de finalizar.
            </p>
          )}
        </CardGrafico>
      </div>

      {/*
        ⚠️ O detalhamento sai da MATRIZ que a resposta já traz — nenhuma consulta nova. Ela é
        exatamente "horas do colaborador por esteira", que é a pergunta de quem clica no nome.
      */}
      {prazoFoco && (
        <DetalheTabelaModal
          titulo={`Cumprimento de prazo — ${prazoFoco.esteira}`}
          apoio={ABAS_PRAZO.find((a) => a.id === abaPrazo)?.apoio}
          abas={ABAS_PRAZO.map((a) => ({
            id: a.id,
            rotulo: `${a.rotulo} (${prazoFoco.passagens.filter((p) => p.situacao === a.id).length})`,
          }))}
          abaAtiva={abaPrazo}
          onTrocarAba={(id) => setAbaPrazo(id as typeof abaPrazo)}
          itens={prazoFoco.passagens.filter((p) => p.situacao === abaPrazo)}
          chave={(p, indice) => `${p.plate}-${indice}`}
          vazio="Nenhuma passagem nesta situação."
          colunas={[
            { titulo: 'Placa', largura: 120, texto: (p) => p.plate },
            { titulo: 'Tempo na esteira', largura: 160, texto: (p) => `${p.diasNaEsteira} dias` },
            {
              titulo: 'Prazo previsto',
              largura: 150,
              texto: (p) => (p.prazoPrevistoDias != null ? `${p.prazoPrevistoDias} dias` : '—'),
            },
          ]}
          onFechar={() => setPrazoFoco(null)}
          sidebarRef={sidebarRef}
        />
      )}

      {colaboradorFoco && (
        <DetalheTabelaModal
          titulo={`Horas de ${colaboradorFoco.nome}`}
          apoio="Onde as horas deste colaborador foram trabalhadas, no recorte selecionado."
          itens={matriz.filter((l) => l.colaboradorId === colaboradorFoco.id)}
          chave={(l) => `${l.esteiraId}-${l.colaboradorId}`}
          vazio="Nenhuma atividade deste colaborador no recorte."
          colunas={[
            { titulo: 'Esteira', largura: 220, texto: (l) => l.esteira },
            { titulo: 'Horas', largura: 120, texto: (l) => `${formatarNumero(l.horasTrabalhadas)} h` },
            { titulo: 'Atividades', largura: 120, texto: (l) => formatarNumero(l.sessoes) },
            { titulo: 'Média por atividade', largura: 170, texto: (l) => `${l.mediaHorasPorSessao} h` },
          ]}
          onFechar={() => setColaboradorFoco(null)}
          sidebarRef={sidebarRef}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Tempos e Equipe"
        secoes={helpSectionsSla}
      />
    </div>
  );
};

export default SlaTab;
